"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { Button, Heading } from "@app/_components/ui";
import { Input, Select } from "@components/inputs";
import { useForm, ValidationError } from "@formspree/react";
import { useMemo, useState } from "react";
import xss from "xss";

import { cn } from "@/lib/utils";

const TRADE_ACCOUNT_FORM_ID = "mbldkyoj";

type TradeAccountFormProps = {
  className?: string;
  reference?: string;
};

export default function TradeAccountForm({
  className,
  reference
}: TradeAccountFormProps) {

  const t = useTranslate();
  const [state, handleSubmit] = useForm(reference ?? TRADE_ACCOUNT_FORM_ID);
  const [email, setEmail] = useState<string | undefined>(undefined);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setEmail(event.target.value);
  }

  const companyTypeOptions = useMemo(
    () => [
      {
        id: 1,
        value: t("tradeAccountCompanyTypeOption1"),
        label: t("tradeAccountCompanyTypeOption1"),
      },
      {
        id: 2,
        value: t("tradeAccountCompanyTypeOption2"),
        label: t("tradeAccountCompanyTypeOption2"),
      },
      {
        id: 3,
        value: t("tradeAccountCompanyTypeOption3"),
        label: t("tradeAccountCompanyTypeOption3"),
      },
      {
        id: 4,
        value: t("tradeAccountCompanyTypeOption4"),
        label: t("tradeAccountCompanyTypeOption4"),
      },
      {
        id: 5,
        value: t("tradeAccountCompanyTypeOption5"),
        label: t("tradeAccountCompanyTypeOption5"),
      },
      {
        id: 6,
        value: t("tradeAccountCompanyTypeOption6"),
        label: t("tradeAccountCompanyTypeOption6"),
      },
      {
        id: 7,
        value: t("tradeAccountCompanyTypeOption7"),
        label: t("tradeAccountCompanyTypeOption7"),
      },
      {
        id: 8,
        value: t("tradeAccountCompanyTypeOption8"),
        label: t("tradeAccountCompanyTypeOption8"),
      },
    ],
    [t],
  );

  const tradeAccountFormSuccessText = useMemo(
    () =>
      t("tradeAccountFormSuccessText", {
        email: `<strong>${email}</strong>`,
      }),
    [email, t],
  );

  if (state.succeeded) {
    return (
      <div className={cn("flex flex-col gap-3", className)}>
        <Heading
          size={{
            initial: 5,
            lg: 4,
          }}
        >
          {t("tradeAccountFormSuccessHeading")}
        </Heading>
        {tradeAccountFormSuccessText && (
          <p
            className="text-base"
            dangerouslySetInnerHTML={{
              __html: xss(tradeAccountFormSuccessText),
            }}
          />
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={cn("flex flex-col gap-3", className)}
    >
      <Heading className="mb-2" size={5}>
        {t("tradeAccountFormHeading") ?? "Apply here"}
      </Heading>
      <div>
        <Input
          type="text"
          name="contactName"
          id="contactName"
          label={t("tradeAccountContactNameLabel") ?? "Contact name"}
          required
          fullWidth
        />
        <ValidationError field="contactName" errors={state.errors} />
      </div>
      <div>
        <Input
          type="text"
          name="companyName"
          id="companyName"
          label={t("tradeAccountCompanyNameLabel") ?? "Company name"}
          required
          fullWidth
        />
        <ValidationError field="companyName" errors={state.errors} />
      </div>
      <div>
        <Select
          classNames={{
            button: "h-10 lg:h-10",
          }}
          name="companyType"
          options={companyTypeOptions}
          placeholder={
            t("tradeAccountFormCompanyTypePlaceholder") ?? "Company type"
          }
          required
        >
          <ValidationError field="companyType" errors={state.errors} />
        </Select>
      </div>
      <div>
        <Input
          type="email"
          name="email"
          id="email"
          label={t("tradeAccountEmailLabel") ?? "Email"}
          required
          fullWidth
          onChange={(event) => handleChange(event)}
        />
        <ValidationError field="email" errors={state.errors} />
      </div>
      <div>
        <Input
          type="tel"
          name="phoneNumber"
          id="phoneNumber"
          label={t("tradeAccountPhoneNumberLabel") ?? "Phone number"}
          required
          fullWidth
        />
        <ValidationError field="phoneNumber" errors={state.errors} />
      </div>
      <div>
        <Button
          className="w-full"
          variant="primary"
          size="lg"
          type="submit"
          isLoading={state.submitting}
        >
          {t("tradeAccountFormSubmitButton") ?? "Submit application"}
        </Button>
      </div>
      <p className="mt-2 text-sm">
        {t("tradeAccountFormPrivacyText") ??
          "By submitting your application, you agree to our Privacy Policy and Terms & Conditions"}
      </p>
    </form>
  );
}
